import { useCallback, useMemo, useState } from 'react';
import { DSButton, DSIcon, DSTypography } from '@eiv2/design-system';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { RequestState } from '../components/RequestState';
import { StatusChip } from '../components/StatusChip';
import { DataGridFrame, visibleDataGridRows } from '../components/DataGridFrame';
import { adminApi } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { formatDateTime, formatNumber } from '../services/format';
import { getEventNameLabel, getStepLabel } from '../services/labels';
import { dataGridLocaleText } from '../theme/dataGridLocale';

export function CheckoutDetailsPage() {
  const navigate = useNavigate();
  const { checkoutId = '' } = useParams<{ checkoutId: string }>();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') ?? undefined;
  const organizationId = searchParams.get('organizationId') ?? undefined;
  const journeyId = searchParams.get('journeyId') ?? undefined;
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  });

  const loader = useCallback(
    (signal: AbortSignal) =>
      adminApi.getCheckout(checkoutId, { limit: 200, source, organizationId, journeyId }, signal),
    [checkoutId, source, organizationId, journeyId]
  );
  const detail = useApiResource(loader, [checkoutId, source, organizationId, journeyId]);
  const eventRows = useMemo(
    () => detail.data?.events.map((e) => ({ ...e, id: e.eventId })) ?? [],
    [detail.data?.events]
  );

  if (detail.error) {
    return (
      <div>
        <PageHeader title="Detalhes do carrinho" subtitle={`Carrinho ${checkoutId}`} />
        <RequestState
          title="Não foi possível carregar o carrinho"
          error={detail.error}
          onRetry={detail.reload}
        />
      </div>
    );
  }

  if (detail.isLoading || !detail.data) {
    return (
      <div>
        <PageHeader title="Detalhes do carrinho" subtitle={`Carrinho ${checkoutId}`} />
        <RequestState
          title="Carregando detalhes"
          message="Buscando a linha do tempo e o resumo do carrinho."
        />
      </div>
    );
  }

  const data = detail.data;

  const eventColumns: GridColDef[] = [
    {
      field: 'eventName',
      headerName: 'Evento',
      flex: 1,
      minWidth: 180,
      valueFormatter: (value: string) => getEventNameLabel(value)
    },
    { field: 'page', headerName: 'Página', flex: 1, minWidth: 180 },
    {
      field: 'step',
      headerName: 'Etapa',
      flex: 1,
      minWidth: 140,
      valueFormatter: (value: string | null) => getStepLabel(value)
    },
    {
      field: 'occurredAt',
      headerName: 'Quando',
      flex: 1.2,
      minWidth: 220,
      valueFormatter: (value: string) => formatDateTime(value)
    }
  ];

  return (
    <div>
      <PageHeader
        title={`Detalhes do carrinho ${data.checkoutId ?? checkoutId}`}
        subtitle={`Sessão ${data.sessionId} · Anônimo ${data.anonymousId ?? 'n/d'}`}
        action={
          <DSButton
            className="w-auto"
            onClick={() => navigate('/checkouts')}
            type="button"
            variant="secondary"
          >
            <DSIcon name="ArrowLeft" size={18} />
            Voltar
          </DSButton>
        }
      />

      <SectionCard
        title="Resumo"
        subtitle="Principais sinais do carrinho selecionado"
        action={<StatusChip status={data.status} />}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <DSTypography as="p" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-5">
              Eventos
            </DSTypography>
            <DSTypography as="p" className="text-[24px] font-bold text-dark-3">
              {formatNumber(data.eventCount)}
            </DSTypography>
          </div>
          <div>
            <DSTypography as="p" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-5">
              Primeira atividade
            </DSTypography>
            <DSTypography as="p" className="text-[16px] font-semibold text-dark-3">
              {formatDateTime(data.firstEventAt)}
            </DSTypography>
          </div>
          <div>
            <DSTypography as="p" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-5">
              Última atividade
            </DSTypography>
            <DSTypography as="p" className="text-[16px] font-semibold text-dark-3">
              {formatDateTime(data.lastEventAt)}
            </DSTypography>
          </div>
        </div>
      </SectionCard>

      <div className="mt-6">
        <SectionCard title="Linha do tempo" subtitle="Eventos brutos de tracking deste checkout">
          <DataGridFrame
            visibleRowCount={visibleDataGridRows(
              eventRows.length,
              paginationModel.page,
              paginationModel.pageSize
            )}
          >
            <DataGrid
              rows={eventRows}
              columns={eventColumns}
              disableRowSelectionOnClick
              localeText={dataGridLocaleText}
              pageSizeOptions={[10, 25, 50]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          </DataGridFrame>
        </SectionCard>
      </div>
    </div>
  );
}
